import { take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { LeadsService } from '../../../shared/services/leads.service';
import { Component, OnInit } from '@angular/core';
import { deliveryStatusLabel } from '../../../shared/utils';
import { Control } from '../../../models/Control';
import { SequenceSentStatusId } from '../../../models/SequenceSentStatus';
import { User } from 'src/app/models/User';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ADMINISTRATOR } from 'src/app/shared/services/user.service';
import {
  RCS_MESSAGE_STATUS_LABELS,
  RCS_READ_CONDITION_SMS_STATUS_LABELS,
  RCS_READ_CONDITION_STATUS_LABELS,
  RcsFallbackReason,
  RcsReadConditionStatus,
} from 'src/app/models/Rcs';
import { rcsFallbackAdminDetail, rcsFallbackNotice } from 'src/app/shared/rcs/rcs-fallback-notice';

/** Canal do passo na linha do tempo: dá a cor do cartão, o ícone e o nome. */
export type LeadStepKind = 'sms' | 'whatsapp' | 'rcs' | 'ai' | 'call' | 'webhook';

const STEP_ICONS: Record<LeadStepKind, string> = {
  sms: 'fa-comment', whatsapp: 'fa-whatsapp', rcs: 'fa-commenting', ai: 'fa-magic', call: 'fa-phone', webhook: 'fa-link',
};

const STEP_NAMES: Record<LeadStepKind, string> = {
  sms: 'Mensagem SMS', whatsapp: 'WhatsApp', rcs: 'Mensagem RCS', ai: 'Conversa com IA', call: 'Mensagem de voz', webhook: 'Webhook',
};

/** Bloco que o `GET /leads/{lead}/sequences` manda na etapa RCS (null enquanto não saiu). */
export interface LeadRcsDelivery {
  type: 'basic' | 'single' | null;
  channel: 'rcs' | 'sms' | null;
  fallback_reason: RcsFallbackReason | null;
  status: string | null;
  delivered_at: string | null;
  read_at: string | null;
  clicks: number | null;
  buttons?: Array<{ type: 'url' | 'pix' | 'call'; label: string; resolved: string }>;
  provider_name?: string | null;
  provider_account_name?: string | null;
  /** Texto que o lead recebeu: o card resolvido ou, se virou SMS, o SMS gerado. */
  text?: string | null;
}

/** `read_condition` da linha RCS (F7.5): o desfecho de "se não ler em X h, enviar SMS". */
export interface LeadRcsReadCondition {
  status: RcsReadConditionStatus;
  due_at: string | null;
  reason: string | null;
  sms_text: string | null;
  sms_sent_at: string | null;
  /** Entrega do SMS da condição pelo DLR da operadora; null enquanto não saiu. */
  sms_status?: string | null;
  sms_delivered_at?: string | null;
  /** Cliques no link do SMS da condição (lidos do encurtador). */
  sms_clicks?: number | null;
}

@Component({
  selector: 'app-lead-sequences',
  templateUrl: './lead-sequences.component.html',
  styleUrls: ['./lead-sequences.component.scss']
})
export class LeadSequencesComponent implements OnInit {

  leadId: string;

  /** Sem lead na rota não há chamada — e sem chamada não há o que anunciar. */
  loading = false;
  sequences$: Observable<any[]>;
  user: User;
  conversationId: string | null = null;

  constructor(
    private leadService: LeadsService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router
  ) {
    this.route.params
      .pipe(take(1))
      .subscribe(params => {
        if (params.id) {
          this.leadId = params.id;
        }
      });
  }

  get isAdministrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  ngOnInit(): void {
    if (this.leadId) {
      this.loading = true;
      // Desliga na EMISSÃO, que é assíncrona — fora do ciclo que leu a flag.
      this.sequences$ = this.leadService.listSequences(this.leadId).pipe(
        tap({
          next: () => { this.loading = false; },
          error: () => { this.loading = false; },
        })
      );

      this.leadService.getConversation(this.leadId).subscribe(
        res => this.conversationId = res?.conversation?.id ?? null,
        () => this.conversationId = null
      );
    }

    this.user = this.authService.getUser(true);
  }

  back(): void {
    window.history.back();
  }

  openConversation(): void {
    if (this.conversationId) {
      this.router.navigate(['/conversations', this.conversationId]);
    }
  }

  /** Etapa RCS (o backend devolve `controls_rcs`; a tela da automação usa `rcs`). */
  isRcs(control: any): boolean {
    return ['controls_rcs', 'rcs'].includes(control?.sequence?.sequence_type);
  }

  channelKind(control: any): LeadStepKind {
    if (control?.is_call4u) {
      return 'call';
    }
    if (control?.is_webhook) {
      return 'webhook';
    }
    if (this.isAi(control)) {
      return 'ai';
    }
    if (this.isRcs(control)) {
      return 'rcs';
    }
    return control?.sequence?.sequence_type === 'whatsapp' ? 'whatsapp' : 'sms';
  }

  channelIcon(control: any): string {
    return STEP_ICONS[this.channelKind(control)];
  }

  channelName(control: any): string {
    return STEP_NAMES[this.channelKind(control)];
  }

  /**
   * Cor da pílula de status, pelo que o status diz (os nomes vêm de tabelas diferentes:
   * `sequence_sent_status`, Call4U e webhook): cancelado/falha em vermelho, saiu em verde,
   * o resto (aguardando, na fila) em laranja.
   */
  statusTone(control: any): 'ok' | 'danger' | 'warn' {
    const text = (this.status(control) || '').toLowerCase();
    if (control?.cancelled || this.aiCancelled(control) || this.isCancelled(control) || /cancel|falh|erro|recus|não atend/.test(text)) {
      return 'danger';
    }
    if (control?.sent || /envi|entreg|atend|lid|sucesso|conclu/.test(text)) {
      return 'ok';
    }
    return 'warn';
  }

  rcsText(control: any): string {
    return (control?.rcs?.channel === 'rcs' ? control.rcs.text : null)
      || control?.message || control?.sequence?.rcs_content?.text || control?.sequence?.text || '';
  }

  rcsButtonHref(button: { resolved: string }): string | null {
    return /^https?:\/\//i.test(button?.resolved || '') ? button.resolved : null;
  }

  /** Canal que saiu de fato: o card RCS (Basic/Single) ou o SMS gerado dele. */
  rcsChannelLabel(control: any): string {
    const rcs: LeadRcsDelivery | null = control?.rcs || null;
    if (!rcs || !rcs.channel) {
      return 'Ainda não saiu';
    }
    if (rcs.channel === 'sms') {
      return 'SMS';
    }
    return rcs.type === 'basic' ? 'RCS Basic' : rcs.type === 'single' ? 'RCS Single' : 'RCS';
  }

  /** Aviso para o cliente quando a etapa RCS saiu por SMS ("Enviado por SMS: este número não tem RCS"). */
  rcsNotice(control: any): string {
    return rcsFallbackNotice(control?.rcs?.fallback_reason as RcsFallbackReason | null);
  }

  /** Motivo técnico de ter virado SMS: só aparece no bloco do Admin. */
  rcsReasonLabel(control: any): string | null {
    return rcsFallbackAdminDetail(control?.rcs?.fallback_reason as RcsFallbackReason | null);
  }

  readConditionLabel(condition: LeadRcsReadCondition): string {
    return RCS_READ_CONDITION_STATUS_LABELS[condition?.status] || condition?.status || '';
  }

  readConditionSmsStatusLabel(condition: LeadRcsReadCondition): string | null {
    const status = condition?.sms_status;
    return status ? (RCS_READ_CONDITION_SMS_STATUS_LABELS[status] || status) : null;
  }

  rcsStatusLabel(control: any): string | null {
    const status = control?.rcs?.status;
    return status ? (RCS_MESSAGE_STATUS_LABELS[status] || status) : null;
  }

  isAi(control: any): boolean {
    return control.sequence?.sequence_type === 'controls_ai';
  }

  /** AI controls have no `cancelled` boolean — a stored reason means it was canceled. */
  aiCancelled(control: any): boolean {
    return this.isAi(control) && !!control.reason;
  }

  /**
   * O control foi cancelado — nos três canais.
   *
   * `controls` (SMS) tem a flag `cancelled`; os canais da Meta não têm, o estado deles é o
   * `sequence_sent_status_id`. A tela só olhava a flag, então uma sequência de WhatsApp
   * cancelada aparecia com "Será enviada em" e SEM o bloco de Motivo — justo o campo que
   * diz o que a Meta respondeu e resolve o problema de quem está olhando.
   */
  isCancelled(control: any): boolean {
    if (control.sequence?.sequence_type === 'whatsapp' || this.isAi(control)) {
      return control.sequence_sent_status_id === SequenceSentStatusId.CANCELED;
    }

    return !!control.cancelled;
  }

  /**
   * A mensagem já saiu, mesmo que a Meta ainda não tenha confirmado a entrega.
   *
   * Nos canais da Meta o envio é síncrono: o control nasce em SENT_WITHOUT_CONFIRMATION (3)
   * com o wamid gravado e só vira SENT (4) quando chega o callback `delivered`. Sem isto a
   * tela mostraria "Será enviada em" para uma mensagem que o lead já recebeu.
   */
  alreadySent(control: any): boolean {
    if (control.sequence?.sequence_type === 'whatsapp' || this.isAi(control)) {
      return [
        SequenceSentStatusId.SENT_WITHOUT_CONFIRMATION,
        SequenceSentStatusId.SENT,
      ].includes(control.sequence_sent_status_id);
    }

    return this.status(control) === 'Enviado';
  }

  status(control: any): string {
    if (control.is_call4u) {
      return control.call4u_status_label || 'Desconhecido';
    }

    if (control.is_webhook) {
      return control.webhook_status_label || 'Desconhecido';
    }

    // RCS, como WhatsApp e IA, não tem `sent`/`queued`: o estado vem em `sequence_sent_status`.
    if (control.sequence?.sequence_type === 'whatsapp' || control.sequence?.sequence_type === 'controls_ai' || this.isRcs(control)) {
      return control.sequence_sent_status?.name || '';
    }

    if (!control.sent && !control.cancelled && !control.queued) {
      return 'Aguardando horário';
    }

    if (!control.sent && !control.cancelled && control.queued) {
      return 'Agendada';
    }

    if (control.cancelled) {
      return 'Cancelada';
    }

    if (control.sent) {
      return 'Enviada';
    }

    return '';
  }

  /** Status de entrega do envio (DLR) — ver deliveryStatusLabel em utils. */
  getDeliveryStatus(control: Control): string {
    return deliveryStatusLabel(
      control?.delivery_status,
      control?.provider_status_description,
      control?.webhook_status,
    );
  }

}
